import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "./api.ts";
import { pageStatus, whereLabel, patientLabel, serviceLabel, type Page } from "@pagelink/shared";
import { Avatar, Btn, Icon, Note, when } from "./ui.tsx";

/** How a team page stands, across every member's copy. */
export interface TeamRollup { total: number; contacted: number; resolved: boolean }

/**
 * A team page is one copy per member. The sender sees it as one card: the
 * first copy stands for the rest, with the roll-up beside it. Copies sent to
 * the viewer, and ordinary pages, pass through unchanged.
 */
export function collapseTeams(pages: Page[], meId: string): { page: Page; team?: TeamRollup }[] {
  const groups = new Map<string, Page[]>();
  for (const p of pages) if (p.broadcastId && p.fromId === meId) groups.set(p.broadcastId, [...(groups.get(p.broadcastId) ?? []), p]);
  const seen = new Set<string>();
  const out: { page: Page; team?: TeamRollup }[] = [];
  for (const p of pages) {
    const g = p.broadcastId && p.fromId === meId ? groups.get(p.broadcastId) : undefined;
    if (!g) { out.push({ page: p }); continue; }
    if (seen.has(p.broadcastId!)) continue;
    seen.add(p.broadcastId!);
    out.push({ page: p, team: {
      total: g.length,
      contacted: g.filter((x) => x.events.some((e) => e.type === "ACK")).length,
      resolved: g.every((x) => pageStatus(x).resolved),
    } });
  }
  return out;
}

/* ---------------------------------------------------------------------------
 * A page as a chat row: who, the service, the newest message (or what the
 * page is about), when, and how it stands. Outgoing pages show ticks as a
 * messenger does: one for sent, two for delivered, two in colour for seen.
 * Colour never stands alone; the status is always written beside it.
 * ------------------------------------------------------------------------ */
export function PageRow({ page, direction, onOpen, starred, onStar, muted, team, meId }: {
  page: Page; direction: "in" | "out"; onOpen: () => void; starred?: boolean; onStar?: () => void; muted?: boolean; team?: TeamRollup; meId?: string;
}) {
  const s = pageStatus(page);
  const open = team ? !team.resolved : !s.resolved;
  // A STAT page is red until it is finished, whatever its status or direction.
  const live = open && page.urgency === "STAT" && !muted;
  // An outgoing page still waiting on someone stands apart from finished ones.
  const pending = direction === "out" && !muted && open && !live;
  // An incoming page you have not finished yet: still yours to deal with.
  const todo = direction === "in" && !muted && open && !live;
  const who = team ? `${page.service} group · ${team.total}` : direction === "in" ? page.fromName : (page.toName ?? "Unassigned · Locating");
  const svc = page.teamId ? (direction === "in" ? `Group page · ${page.service}` : "Group page") : serviceLabel(page.service, page.subService, page.serviceShort);
  const last = page.lastMessage;
  const preview = last
    ? `${last.authorId === meId ? "You: " : ""}${last.body}`
    : page.context ?? (patientLabel(page) ?? `${page.consultType} · ${whereLabel(page)}`);
  const at = last?.at ?? page.sentAt;
  const status = team ? `${team.contacted} of ${team.total} answered` : s.label;
  const ticks = direction === "out" && !team && open ? (s.rank >= 2 ? "✓✓" : s.state === "delivered" ? "✓✓" : "✓") : null;
  return (
    // The whole row opens the page for a pointer. For keyboards and screen
    // readers the name is the one button, so the star is not nested inside
    // another control (WCAG 4.1.2).
    <div className={`crow${live ? " live" : ""}${pending ? " pending" : ""}${todo ? " todo" : ""}`} onClick={onOpen}>
      <Avatar id={team ? undefined : (direction === "in" ? page.fromId : page.toId) ?? undefined} name={who} size={46} fontSize={17} />
      <div className="crow-main">
        <div className="crow-l1">
          <button type="button" className="crow-name card-link"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            aria-label={`${who}, ${svc}: ${status}, ${page.urgency}${page.unread > 0 ? `, ${page.unread} unread` : ""}. Open page`}>
            {who}<span className="crow-svc"> · {svc}</span>
          </button>
          <span className={`crow-time${page.unread > 0 ? " unread" : ""}`}>{when(at)}</span>
        </div>
        <div className="crow-l2">
          <span className={`crow-st st-${team ? (team.resolved ? "done" : "accepted") : s.state}`}>
            {ticks && <span className={`ticks${s.rank >= 2 ? " seen" : ""}`} aria-hidden="true">{ticks}</span>}{status}
          </span>
          {page.urgency === "STAT" && <span className="crow-stat">STAT</span>}
          <span className="crow-prev">{preview}</span>
          {page.unread > 0 && <span className="crow-badge" aria-hidden="true">{page.unread}</span>}
          {onStar && (
            <button className="star-btn sm" aria-pressed={!!starred} aria-label={starred ? "Remove star" : "Star this page"}
              onClick={(e) => { e.stopPropagation(); onStar(); }}>
              <Icon name="star" size={16} sw={1.8} fill={starred ? "currentColor" : "none"} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function DirTabs({ value, setValue, inCount, outCount }: {
  value: "in" | "out"; setValue: (v: "in" | "out") => void; inCount: number; outCount: number;
}) {
  return (
    <div className="dirtabs">
      <div className="seg" role="tablist" aria-label="Page direction">
        {([["in", "Incoming", inCount], ["out", "Outgoing", outCount]] as const).map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={value === k} onClick={() => setValue(k)}>
            <Icon name={k === "in" ? "incoming" : "outgoing"} size={16} sw={2.3} />{label}{n > 0 && <span className="n">{n}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

type View = "open" | "finished" | "archived";

export function PageList({ incoming, outgoing, meId, tab, setTab, onOpen, onCompose, stars, onStar, onChanged }: {
  incoming: Page[]; outgoing: Page[]; meId: string; tab: "in" | "out"; setTab: (t: "in" | "out") => void;
  onOpen: (id: string) => void; onCompose: () => void; stars: Set<string>; onStar: (id: string) => void;
  onChanged: () => Promise<void>;
}) {
  const [view, setView] = useState<View>("open");
  const [archived, setArchived] = useState<Page[] | null>(null);
  const [swiped, setSwiped] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; undoId?: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Archived pages are not in the live lists; fetch them when the tab opens.
  const loadArchived = () => api.search({ direction: tab, archived: "yes" }).then((r) => setArchived(r.pages)).catch(() => setArchived([]));
  useEffect(() => { if (view === "archived") { setArchived(null); void loadArchived(); } }, [view, tab]);

  const say = (text: string, undoId?: string) => {
    setNotice({ text, undoId });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setNotice(null), 6000);
  };
  const refresh = async () => { await onChanged(); if (view === "archived") await loadArchived(); };
  const hide = async (p: Page, kind: "archived" | "deleted") => {
    setSwiped(null);
    if (kind === "deleted" && !window.confirm("Delete this page from your lists? It is gone from Pages and History for you. The other doctor keeps it, and the hospital record is unchanged.")) return;
    try {
      await api.hide(p.id, kind);
      await refresh();
      say(kind === "archived" ? "Moved to the bin." : "Deleted from your lists.", p.id);
    } catch (e) {
      say(e instanceof ApiError ? e.message : "That did not work.");
    }
  };
  const unhide = async (id: string, text?: string) => {
    setSwiped(null);
    setNotice(null);
    await api.unhide(id).catch(() => {});
    await refresh();
    if (text) say(text);
  };
  // Cancel a page I sent, from the list: every still-open copy of a group page.
  const cancelFromList = async (p: Page, thenArchive: boolean) => {
    setSwiped(null);
    const to = p.teamId ? "everyone in the group still holding it" : (p.toName ?? "the recipient");
    if (!window.confirm(`${thenArchive ? "Cancel this page and move it to the bin" : "Cancel this page"}? ${to} will be told it is no longer needed.`)) return;
    const copies = p.broadcastId ? outgoing.filter((x) => x.broadcastId === p.broadcastId && !pageStatus(x).resolved) : [p];
    try {
      for (const c of copies) await api.cancel(c.id, "cancel-not-needed");
      if (thenArchive) await api.hide(p.id, "archived");
      await refresh();
      say(thenArchive ? "Cancelled and moved to the bin. A cancel cannot be undone." : "Cancelled. It is under Finished.");
    } catch (e) {
      say(e instanceof ApiError ? e.message : "That did not work.");
    }
  };
  const isOpen = (r: { page: Page; team?: TeamRollup }) => (r.team ? !r.team.resolved : !pageStatus(r.page).resolved);
  const rowsIn: { page: Page; team?: TeamRollup }[] = incoming.map((page) => ({ page }));
  const rowsOut = collapseTeams(outgoing, meId);
  const all = tab === "in" ? rowsIn : rowsOut;
  const openRows = all.filter(isOpen);
  const finishedRows = all.filter((r) => !isOpen(r));
  const archivedRows: { page: Page; team?: TeamRollup }[] = archived ? (tab === "in" ? archived.map((page) => ({ page })) : collapseTeams(archived, meId)) : [];
  const list = view === "open" ? openRows : view === "finished" ? finishedRows : archivedRows;
  const openIn = rowsIn.filter(isOpen).length;
  const openOut = rowsOut.filter(isOpen).length;

  const empty = view === "open"
    ? (tab === "in" ? "Nothing waiting for you." : "Nothing you sent is still open.")
    : view === "finished" ? "No finished pages here. Ones you binned are in the bin."
    : "The bin is empty.";

  return (
    <>
      <Btn full lg onClick={onCompose} style={{ marginBottom: 2 }}><Icon name="plus" size={16} sw={2.4} />Page someone</Btn>
      <DirTabs value={tab} setValue={(t) => { setTab(t); setSwiped(null); }} inCount={openIn} outCount={openOut} />
      <div className="subtabs" role="tablist" aria-label="Which pages">
        {([["open", "Open", openRows.length], ["finished", "Finished", finishedRows.length]] as const).map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={view === k} onClick={() => { setView(k); setSwiped(null); }}>
            {label}{n ? <span className="n">{n}</span> : null}
          </button>
        ))}
        <button role="tab" className="bin-tab" aria-selected={view === "archived"} aria-label="Bin" title="Bin"
          onClick={() => { setView("archived"); setSwiped(null); }}>
          <Icon name="trash" size={18} sw={2} />
        </button>
      </div>
      {view === "archived" && !archived ? (
        <div className="fine" style={{ textAlign: "left" }}>Loading…</div>
      ) : list.length === 0 ? (
        view === "open" && all.length > 0 ? (
          <div className="empty-done">
            <div className="tick"><Icon name="check" size={26} sw={2.2} /></div>
            <div className="t">You're caught up</div>
            <div className="s">Finished pages are under Finished</div>
          </div>
        ) : <Note>{empty}</Note>
      ) : (
        <div className="chatlist">
          {list.map(({ page: p, team }) => {
            const row = (
              <PageRow page={p} direction={tab} team={team} meId={meId} onOpen={() => onOpen(p.id)}
                starred={stars.has(p.id)} onStar={() => onStar(p.id)} />
            );
            // Open pages sent to you stay put until answered or declined. Open
            // pages you sent can be cancelled, or cancelled and archived.
            if (view === "open" && tab === "in") return <div key={p.id} className="swipe">{row}</div>;
            if (view === "open") return (
              <SwipeRow key={p.id} open={swiped === p.id} onOpenChange={(o) => setSwiped(o ? p.id : null)}
                actions={<>
                  <button className="sw-archive" onClick={() => void cancelFromList(p, true)}><Icon name="trash" size={17} sw={2} />Bin</button>
                  <button className="sw-delete" onClick={() => void cancelFromList(p, false)}><Icon name="x" size={17} sw={2.4} />Cancel</button>
                </>}>
                {row}
              </SwipeRow>
            );
            return (
              <SwipeRow key={p.id} open={swiped === p.id} onOpenChange={(o) => setSwiped(o ? p.id : null)}
                actions={view === "finished" ? <>
                  <button className="sw-delete" onClick={() => void hide(p, "archived")}><Icon name="trash" size={17} sw={2} />Bin</button>
                </> : <>
                  <button className="sw-archive" onClick={() => void unhide(p.id, "Restored to Finished.")}><Icon name="restore" size={17} sw={2} />Restore</button>
                  <button className="sw-delete" onClick={() => void hide(p, "deleted")}><Icon name="x" size={17} sw={2.4} />Delete</button>
                </>}>
                {row}
              </SwipeRow>
            );
          })}
        </div>
      )}
      {list.length > 0 && !(view === "open" && tab === "in") && (
        <div className="fine" style={{ marginTop: 6 }}>
          Swipe a page left, or open it, to {view === "open" ? "cancel it or move it to the bin" : view === "finished" ? "move it to the bin" : "restore or delete it"}.
        </div>
      )}
      {notice && (
        <div className="undo-bar" role="status">
          <span>{notice.text}</span>
          {notice.undoId && <button className="linkish" onClick={() => void unhide(notice.undoId!)}>Undo</button>}
        </div>
      )}
    </>
  );
}

/* Swipe left to reveal actions behind a card. Vertical scrolling is left to
 * the browser; a horizontal drag past halfway snaps open, anything less snaps
 * back. A drag never counts as a tap on the card. */
const SW = 168;
function SwipeRow({ open, onOpenChange, actions, children }: {
  open: boolean; onOpenChange: (open: boolean) => void; actions: ReactNode; children: ReactNode;
}) {
  const start = useRef<{ x: number; y: number; base: number } | null>(null);
  const dragged = useRef(false);
  const [dx, setDx] = useState<number | null>(null);
  const x = dx ?? (open ? -SW : 0);

  return (
    <div className="swipe">
      {/* Out of reach until swiped open: not focusable, not read out. */}
      <div className="swipe-actions" aria-hidden={!open} inert={!open}>{actions}</div>
      <div className="swipe-front" style={{ transform: `translateX(${x}px)`, transition: dx === null ? "transform .18s ease" : "none" }}
        onPointerDown={(e) => { start.current = { x: e.clientX, y: e.clientY, base: open ? -SW : 0 }; dragged.current = false; }}
        onPointerMove={(e) => {
          const s = start.current;
          if (!s) return;
          const mx = e.clientX - s.x, my = e.clientY - s.y;
          if (!dragged.current) {
            if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) { start.current = null; return; }
            if (Math.abs(mx) < 8) return;
            dragged.current = true;
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          }
          setDx(Math.max(-SW - 24, Math.min(0, s.base + mx)));
        }}
        onPointerUp={() => {
          if (dragged.current && dx !== null) onOpenChange(dx < -SW / 2);
          start.current = null;
          setDx(null);
        }}
        onPointerCancel={() => { start.current = null; setDx(null); }}
        onClickCapture={(e) => {
          if (dragged.current) { e.stopPropagation(); e.preventDefault(); dragged.current = false; return; }
          if (open) { e.stopPropagation(); onOpenChange(false); }
        }}>
        {children}
      </div>
    </div>
  );
}
